RivalIQ.apps.Favorites.module('curations', function (curations, App, Backbone, Marionette, $, _) {

    'use strict';

    const DEFAULT_MAX_METRICS = 8;

    const ChartNotFound = RivalIQ.views.ItemView.extend({
        template: '#favoritesChartBuilderChartNotFoundTemplate'
    });

    const ConfirmExitModal = RivalIQ.views.ItemView.mixin({
        className: 'post-tag-manager-exit-confirm',
        template: '#favoritesChartBuilderConfirmExitModalTemplate',

        ui: {
            confirmExitBtn: '.x-confirm-exit',
            cancelBtn: '.x-cancel'
        },

        events: {
            'click @ui.confirmExitBtn': 'onConfirmExit',
            'click @ui.cancelBtn': 'onCancelExit'
        },

        onConfirmExit: function () {
            const me = this;

            RivalIQ.execute('hide:modal');
            const onExitConfirmed = me.getOption('onExitConfirmed');
            onExitConfirmed();
        },

        onCancelExit: function () {
            RivalIQ.execute('hide:modal');
        },

        serializeData: function () {
            const me = this;

            const headerText = (me.getOption('isEditingWidget') ? RivalIQ.strings.favorites_builder_confirm_exit_edit_chart : RivalIQ.strings.favorites_builder_confirm_exit_new_chart);

            return {
                headerText: headerText
            };
        }
    });

    function isWidgetEdited(builder) {
        const widget = builder.getWidget();

        if (!widget) {
            return false;
        }

        const serializedWidget = widget.toJSON();
        _.extend(serializedWidget.props, {
            metrics: builder.getProp('metrics')
        });

        const originalDefinition = _.clone(builder._originalDefinition);

        const currentWidgetProps = JSON.parse(JSON.stringify(serializedWidget.props));
        const originalProps = JSON.parse(JSON.stringify(_.pick(originalDefinition.props, _.keys(currentWidgetProps))));

        if (_.isUndefined(originalProps.useAutoTimeInterval) && !_.isUndefined(currentWidgetProps.useAutoTimeInterval)) {
            originalProps.useAutoTimeInterval = true;
        }

        const propsSame = _.isEqual(currentWidgetProps, originalProps);

        return serializedWidget.widgetId !== originalDefinition.widgetId || !propsSame;
    }

    // This is the curation for creating/editing a dashboard widget
    // It is interesting b/c it acts somewhat like a details view with extra
    // edit chrome
    curations.Builder = RivalIQ.apps.Dashboard.models.Curation.extend({
        title: function () {
            const me = this;
            return me.getName() || RivalIQ.strings.favorites;
        },

        widgetsView: App.views.Layout,

        // Loads the configuration from the server. If this is a new
        // configuration the server responds with the initial props for
        // rendering a widget of type [focusWidgetId]. If we have a widget
        // instanceId it is treated as an edit view.
        loadConfiguration: function () {
            const me = this;

            const curationId = RivalIQ.enums.curation.DASHBOARDS;

            const attrs = {
                id: me.getProp('configurationId') || 'primary',
                instanceId: me.getProp('focusWidgetInstanceId'),
                curation_id: curationId,
                landscape_id: me.getProp('landscapeId')
            };

            let configuration;
            if (attrs.instanceId) {
                configuration = new App.models.EditConfiguration(attrs);
            } else {
                configuration = new App.models.NewConfiguration(attrs);
            }

            const deferred = new $.Deferred();

            configuration.fetch({
                data: {
                    widgetId: me.getProp('focusWidgetId'),
                    widgetGroupId: me.getProp('widgetGroupId')
                }
            }).then(function () {
                // baseMetric is used to extract the common components of
                // props.metrics so that they can be edited from a single
                // dropdown instead of on a per-metric basis
                // For example, the interval dimensions (day, week, month) must
                // be added to all metrics in props.metrics but are controlled
                // from a single dropdown
                me.baseMetric = configuration.get('baseMetric');

                // NOTE we have to extend the definition with the corresponding
                // chart type, as a `processProps` method, or other relevant
                // behavioral override, may be specified there.
                const definitions = _.map(configuration.get('definitions'), function (d) {
                    const chartType = App.data.chartTypes[d.widgetId];
                    // NOTE/TODO this is a hack so that new widgets will
                    // properly flow through the widget instance state filter
                    // in the shell (i.e., we won't call `prepare` on the
                    // Widget if the definition does not have an `instanceId`)
                    if (!d.instanceId) {
                        d.instanceId = 'builder-provisional';
                    }
                    return _.defaults({}, d, chartType);
                });
                configuration.attributes.definitions = definitions;

                deferred.resolve(configuration);
            }).fail(function (error) {
                if (_.get(error, 'status') === 404) {
                    if (attrs.instanceId) {
                        return deferred.resolve(new ChartNotFound());
                    } else {
                        return deferred.resolve(App.utils.getDefaultRedirect());
                    }
                } else {
                    return deferred.reject(error);
                }
            });

            return deferred;
        },

        // This method is called from the form to change the active widget.
        // Doing so is more complicated than just instantiating a new widget of
        // the desired type b/c each widget can have its own unique
        // requirements
        // For example, when switching cross channel engagement rate from a stacked
        // bar to a line chart we need to remove the `channel` dimension and
        // add the `day` dimension. We may also have change the title, need to
        // enforce size restrictions, etc
        changeWidgetId: function (newWidgetId, props) {
            const me = this;

            // because we haven't reset the widget cache, getWidget will still return the old widget; i.e.,
            // newWidgetId !== me.getWidget().widgetId until that cache gets reset later in this method.
            const oldWidget = me.getWidget();
            const oldWidgetProps = oldWidget.getProps();
            const widgetProps = _.pick(oldWidgetProps, 'title', 'columnWidth');
            const definitions = me.buildWidgetDefinitions(newWidgetId, oldWidget.instanceId, widgetProps);

            // Silently update the configuration to get new widget instance w/o
            // triggering re-render
            me.configuration.set({
                definitions: definitions
            }, {
                silent: true
            });
            me.configuration.resetWidgetCache();

            // NOTE we must use `buildWidget` rather than `getWidget` as the
            // latter will trigger a render, and the state of the widget
            // undergoing that process could be all wrong (since we haven't
            // even set up our metrics yet)
            const newWidget = me.configuration.buildWidget(me, newWidgetId);

            const currentBaseMetric = me.baseMetric;
            let metrics = _.get(props, ['metrics'], me.getProp('metrics'));
            let maxMetrics;
            if (_.isFunction(App.data.chartTypes[newWidgetId].maxMetrics)) {
                maxMetrics = App.data.chartTypes[newWidgetId].maxMetrics.apply(newWidget);
            } else {
                maxMetrics = App.data.chartTypes[newWidgetId].maxMetrics;
            }
            maxMetrics = maxMetrics || DEFAULT_MAX_METRICS;
            metrics = _.take(metrics, maxMetrics);

            me.baseMetric = App.data.chartTypes[newWidgetId].getMetricDefaults(metrics);

            metrics = me.buildMetrics(newWidget, metrics, currentBaseMetric);

            let columnWidth = widgetProps.columnWidth;
            if (!newWidget.isPropValueValid('columnWidth', columnWidth)) {
                columnWidth = newWidget.getValidPropValues('columnWidth')[0];
            }

            props = _.defaults({
                metrics: metrics,
                columnWidth: columnWidth
            }, props);


            const benchmarkWidgetProps = _.pick(oldWidgetProps, 'showBenchmarkLine', 'selectedBenchmarkCompareAgainst');
            const widgetSupportsBenchmarkLine = App.data.chartTypes[newWidgetId].widgetSupportsBenchmarkLine;
            let metricSupportsBenchmarkLine = App.data.chartTypes[newWidgetId].metricSupportsBenchmarkLine;
            if (_.isFunction(metricSupportsBenchmarkLine)) {
                metricSupportsBenchmarkLine = metricSupportsBenchmarkLine.call(newWidget);
            }
            if (!widgetSupportsBenchmarkLine || !metricSupportsBenchmarkLine) {
                benchmarkWidgetProps.showBenchmarkLine = false;
                benchmarkWidgetProps.selectedBenchmarkCompareAgainst = null;
            }
            _.extend(props, benchmarkWidgetProps);


            // NOTE abstraction-breaking that we need to do this here, but this is a consequence of the model where there's the builder
            // dashboard model (which drives the fields) and the widget dashboard model (which drives the preview widget we display).
            if (newWidgetId === RivalIQ.enums.widgetTypes.METRIC_COMPANY_LINE_CHART) {
                const hasDefaultInterval = RivalIQ.metric.search.getPropertyValue(_.head(metrics), 'defaultTimeSeriesInterval');
                props.useAutoTimeInterval = (hasDefaultInterval ? undefined : true);
            }

            me.setProps(props, {
                silent: true,
                navigate: false
            });

            // NOTE this will trigger a re-render no matter what, as the widget
            // cache in the configuration will need to be constructed
            me.getWidget();
        },

        // Updates the base metric, which in turn updates all prop.metrics
        // Use this to apply a dimension or property globally to all metrics
        setBaseMetric: function (metric) {
            const me = this;

            const oldBase = me.baseMetric;
            me.baseMetric = metric;

            me.setProps({
                metrics: me.buildMetrics(me.getWidget(), me.getProp('metrics'), oldBase)
            }, {
                navigate: false
            });

            me.trigger('change:baseMetric');
        },

        buildMetrics: function (widget, metrics, parseBaseMetric) {
            const me = this;

            // redact unavailable sources if necessary.
            let availableSources = _.get(App.data.chartTypes, [widget.widgetId, 'availableSources']);
            if (_.isFunction(availableSources)) {
                availableSources = availableSources.call(widget);
            }
            if (availableSources) {
                const sources = _.keyBy(availableSources.map('id'));
                metrics = _.map(metrics, metric => {
                    const metricSource = RivalIQ.metric.property.source(metric);
                    if (_.isUndefined(sources[metricSource])) {
                        metric.channel = undefined;
                        metric.property = 'audience';
                    }

                    return metric;
                });
            }

            const collection = new App.collections.Metrics(metrics, {
                parse: true,
                baseMetric: parseBaseMetric,
                widget: widget
            });

            return collection.toMetrics(me.baseMetric);
        },

        getWidget: function () {
            const me = this;

            const widget = me.getWidgets().first();

            // squirrel away the initial state of the widget, we'll use this later to see if we need to show the user a ConfirmExitModal later.
            if (!me._originalDefinition && widget) {
                me._originalDefinition = {
                    widgetId: widget.widgetId,
                    props: widget.getProps()
                };
            }

            return widget;
        },

        buildWidgetDefinitions: function (widgetId, instanceId, props) {
            const chartType = App.data.chartTypes[widgetId];

            return [
                _.defaults({
                    instanceId: instanceId,
                    widgetId: widgetId,
                    props: props
                }, chartType)
            ];
        },

        appendDefinition: function (child) {
            const me = this;
            return me.configuration.parentConfiguration().appendDefinition(child);
        },

        getValidChannels: function () {
            const me = this;
            return me.getWidget().getValidChannels();
        },

        navigationInterceptor: function (navigateFcn) {
            const me = this;

            if (isWidgetEdited(me)) {
                const modal = new ConfirmExitModal({
                    onExitConfirmed: navigateFcn,
                    isEditingWidget: Boolean(me.getProp('focusWidgetInstanceId'))
                });

                RivalIQ.execute('show:modal', {
                    size: 'small',
                    contentView: modal
                });

                return false;
            } else {
                return true;
            }
        }
    });
});
